import type { ReactElement } from 'react';

import { cn } from '@/components/ui/cn';
import { plural } from '@/lib/plural';

import { issueText } from '../presentation';
import type { ImportPlan } from '../types';

// Paso 2 de "Importar desde Excel": la previsualización del servidor (nada se escribió
// todavía). Qué se creará, y cada fila con errores o avisos en palabras, señalada por su
// fila del Excel. Con errores no se puede importar.

function names(list: string[]): string {
  return list.length <= 5
    ? list.join(', ')
    : `${list.slice(0, 5).join(', ')} y ${list.length - 5} más`;
}

/** Filas con errores o avisos, señaladas por su fila del Excel (previsualización y
 *  resultado comparten la misma lista). */
export function FlaggedRows({
  rows,
  rowNumbers,
}: {
  rows: ImportPlan['rows'];
  rowNumbers: readonly number[];
}): ReactElement | null {
  const flagged = rows.filter((r) => r.issues.length > 0);
  if (flagged.length === 0) {
    return null;
  }
  return (
    <ul className="import-rows">
      {flagged.map((r) => (
        <li key={r.index} className="import-row">
          <div className="import-row-head">
            <span>
              Fila {rowNumbers[r.index] ?? r.index + 2} · <span className="code">{r.code}</span>
            </span>
            <span className={cn('badge', r.status === 'error' ? 'b-red' : 'b-warn')}>
              {r.status === 'error' ? 'Error' : 'Aviso'}
            </span>
          </div>
          <ul className="import-issues">
            {r.issues.map((i, k) => (
              <li key={k}>{issueText(i)}</li>
            ))}
          </ul>
        </li>
      ))}
    </ul>
  );
}

export function ImportPreview({
  plan,
  rowNumbers,
}: {
  plan: ImportPlan;
  rowNumbers: number[];
}): ReactElement {
  const { summary, taxonomyToCreate } = plan;

  return (
    <div className="form-grid">
      <div className="import-summary">
        <strong>{`Se crearán ${plural(summary.entriesToCreate, 'documento', 'documentos')}`}</strong>
        {taxonomyToCreate.areas.length > 0 && (
          <span>
            {`${plural(taxonomyToCreate.areas.length, 'proceso nuevo', 'procesos nuevos')}: ${names(taxonomyToCreate.areas.map((a) => a.name))}`}
          </span>
        )}
        {taxonomyToCreate.types.length > 0 && (
          <span>
            {`${plural(taxonomyToCreate.types.length, 'tipo nuevo', 'tipos nuevos')}: ${names(taxonomyToCreate.types.map((t) => t.name))}`}
          </span>
        )}
        {summary.warnings > 0 && (
          <span className="soft">
            {`${plural(summary.warnings, 'documento con avisos: se importa igual', 'documentos con avisos: se importan igual')}.`}
          </span>
        )}
      </div>
      {summary.errors > 0 && (
        <p className="fld-error" role="alert" style={{ margin: 0 }}>
          {`${plural(summary.errors, 'documento con errores', 'documentos con errores')}: corrige el Excel y vuelve a subirlo.`}
        </p>
      )}
      <FlaggedRows rows={plan.rows} rowNumbers={rowNumbers} />
    </div>
  );
}
